/*
 * pdf.js rendering for the viewer (browser-only). The driver owns load/edit/
 * save; pdf.js is purely for displaying page rasters. Worker is bundled by Vite
 * via the ?url import (vite/client declares the module type).
 */

import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = workerUrl;

export type { PDFDocumentProxy };

/**
 * Load a pdf.js document from PDF bytes. The buffer is cloned first: pdf.js may
 * detach/transfer the ArrayBuffer, which would corrupt the originalBytes the
 * driver needs for a byte-preserving save.
 */
export async function loadViewerDocument(bytes: Uint8Array): Promise<PDFDocumentProxy> {
  return await getDocument({ data: bytes.slice() }).promise;
}

/**
 * Render one page (1-based) into a canvas at the given CSS scale (DPR-aware).
 * `rotation` is the absolute display rotation in degrees; when omitted pdf.js
 * uses the page's intrinsic /Rotate. A 90/270 rotation swaps width/height,
 * which the viewport already accounts for, so the canvas sizes itself from it.
 */
export async function renderPageToCanvas(
  doc: PDFDocumentProxy,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  scale = 1,
  rotation?: number,
): Promise<void> {
  const page = await doc.getPage(pageNumber);
  const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: scale * dpr, ...(rotation === undefined ? {} : { rotation }) });
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d canvas context unavailable');

  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  canvas.style.width = `${Math.round(viewport.width / dpr)}px`;
  canvas.style.height = `${Math.round(viewport.height / dpr)}px`;

  await page.render({ canvasContext: ctx, viewport }).promise;
}
